import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Internal imports - organized by category


// Contexts
import LysepThemeProvider from '../contexts/ThemeProvider';
import { usePermissions } from '../contexts/PermissionContext';
import { Logout as LogoutD } from '../contexts/Reducers/SessionSlice';
import { TaskProvider } from '../contexts/TaskContext';

// Components
import Navbar from '../components/layout/Navbar';
import Sidebar from '../components/layout/Sidebar';
import WrapperPanel from '../components/layout/WrapperPanel';
import PermissionDebugger from '../components/dev/PermissionDebugger';
import SystemHelperPanel from '../components/layout/SystemHelperPanel';

// Layout components
import { SidebarMenu } from './components/SidebarMenu';

// Hooks
import { useFCMNotifications } from './hooks/useFCMNotifications';

// Config & Constants
import { PanelLayoutProvider } from './PanelLayoutContext';
import { getAvailableModules } from './modulesConfig';
import { SIDEBAR_STATE } from './constants';

/**
 * PanelLayout Component
 * Main layout container for the panel application
 * Provides sidebar, navbar, and content wrapper
 */
const PanelLayout = () => {
    // Hooks
    const theme = useTheme();
    const dispatch = useDispatch();
    const { page } = useParams();

    // State
    const session = useSelector(state => state.session);
    const { canAccessModule, loading: permissionsLoading } = usePermissions();
    const [sidebarState, setSidebarState] = useState(SIDEBAR_STATE.COLLAPSED);

    // Sidebar dispatch function (for context compatibility)
    const sidebarDispatch = (state, action) => {
        switch (action.name) {
            case "SET_SIDEBAR":
                setSidebarState(action.data);
                break;
            default:
                break;
        }
    };

    // Sidebar helpers
    const collapseSidebar = () => {
        setSidebarState(SIDEBAR_STATE.COLLAPSED);
    };

    // Effects
    useFCMNotifications(session.Authenticated);

    // Get available modules based on permissions
    const modules = getAvailableModules(canAccessModule);

    // Find current module or default to first
    const currentModule = modules.find(m => m.path === page) || modules[0];

    // Handle logout
    useEffect(() => {
        if (page === "logout") {
            dispatch(LogoutD());
        }
    }, [page, dispatch]);

    // Logging (dev only)
    if (import.meta.env.DEV) {
        console.log("Theme:", theme.palette?.primary?.main);
        console.log("Session authenticated:", session.Authenticated);
        console.log("Permissions loading:", permissionsLoading);
        console.log("Available modules:", modules.map(m => m.title));
        console.log("Rendering:", currentModule?.title);
    }

    // Context value for sidebar state management (backward compatible structure)
    const contextValue = {
        sidebarState,
        dispatch: sidebarDispatch
    };

    return (
        <div className="w-full h-full absolute block bg-[#212529]">
            <LysepThemeProvider>
                <TaskProvider>
                    <PanelLayoutProvider value={contextValue}>
                        {/* Toast Container */}
                        <ToastContainer
                            theme='dark'
                            position="top-right"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={true}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            toastClassName="!bg-[#212529] !text-white"
                        />

                        {/* Backdrop */}
                        <div
                            className={`fixed inset-0 bg-black/40 z-[100] transition-opacity duration-300
                                ${sidebarState === SIDEBAR_STATE.VISIBLE ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                            onClick={collapseSidebar}
                        />

                        {/* Navbar */}
                        <Navbar />

                        {/* Sidebar */}
                        <Sidebar>
                            <SidebarMenu
                                currentPage={page}
                                canAccessModule={canAccessModule}
                            />
                        </Sidebar>

                        {/* Content Wrapper */}
                        <WrapperPanel>
                            <div className="w-full block pt-4">
                                {currentModule?.component}
                                {import.meta.env.DEV && <PermissionDebugger />}
                            </div>
                        </WrapperPanel>

                        {/* Global Task Panel (for root only) */}
                        <SystemHelperPanel />
                    </PanelLayoutProvider>
                </TaskProvider>
            </LysepThemeProvider>
        </div>
    );
};

export default PanelLayout;

// Export context for backward compatibility
export {
    DashboardConsumer,
    DashboardProvider,
    DashboardContext
} from './PanelLayoutContext';
